// Desktop notifications: offered once (people choose), then shown for mentions, replies and direct messages while
// this tab is in the background, unless Do not disturb is on. Clicking one opens the conversation.
import { useState } from "react";
import { useNavigate } from "react-router";
import { NOTIFICATION_LABEL, notificationHref, type Notification as Note } from "../../lib/chat";
import { useLiveEvent } from "../../lib/live";
import { useMe } from "../../lib/session";
import { isDnd } from "../../lib/people";
import { Icon } from "../../components/icons";

const DISMISSED = "collab:notifications-prompt-dismissed";
const supported = () => typeof window !== "undefined" && "Notification" in window;

export function DesktopNotifications({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const me = useMe().data;
  useLiveEvent((e) => {
    if (e.type !== "notification.created" || !supported() || Notification.permission !== "granted" || document.visibilityState === "visible") return;
    if (isDnd(me?.user.dndUntil)) return;
    const n = e.notification as Pick<Note, "id" | "kind" | "conversationId" | "parentId" | "taskId" | "projectId" | "preview"> & { actor: { name: string } };
    try {
      const shown = new Notification(`${n.actor.name} ${NOTIFICATION_LABEL[n.kind] ?? ""}`.trim(), { body: n.preview || undefined, tag: n.id });
      shown.onclick = () => {
        window.focus();
        navigate(notificationHref(slug, n));
        shown.close();
      };
    } catch { /* not allowed in this context */ }
  });
  return null;
}

// A slim banner offering desktop notifications (until turned on, blocked, or dismissed).
export function NotificationsPrompt() {
  const [hidden, setHidden] = useState(() => {
    if (!supported() || Notification.permission !== "default") return true;
    try { return localStorage.getItem(DISMISSED) === "1"; } catch { return false; }
  });
  if (hidden) return null;
  const dismiss = () => { try { localStorage.setItem(DISMISSED, "1"); } catch { /* storage unavailable */ } setHidden(true); };
  return (
    <div className="flex items-center gap-3 border-b border-brand-100 bg-brand-50 px-6 py-2 text-sm text-brand-700 dark:border-brand-700/40 dark:bg-brand-700/20 dark:text-brand-100" role="region" aria-label="Desktop notifications">
      <Icon name="bell" className="h-4 w-4 shrink-0" />
      <span className="flex-1">Get a notification for calls, mentions and direct messages when this tab is in the background.</span>
      <button type="button" onClick={() => { void Notification.requestPermission().finally(() => setHidden(true)); }} className="rounded-md bg-brand-600 px-3 py-1 font-medium text-white hover:bg-brand-700">Turn on</button>
      <button type="button" onClick={dismiss} aria-label="Not now" className="rounded-md p-2 [@media(pointer:coarse)]:p-3 hover:bg-brand-100 dark:hover:bg-brand-700/40"><Icon name="x" className="h-4 w-4" /></button>
    </div>
  );
}
